// Shared one-shot reload guard for stale-chunk recovery after a deploy.
// Used by:
//   - utils/lazyWithRetry.js (clean ChunkLoadError → script onerror path)
//   - src/index.js window.error listener (nginx-served-HTML-as-JS path,
//     where the script "loads" but the browser SyntaxErrors on the first `<`).
//
// The sessionStorage flag prevents an endless reload loop: if the chunk is
// genuinely missing (CDN failure, real bug), the second occurrence skips the
// reload and lets the error surface normally.

const RELOAD_FLAG = "chunkReloadAttempted";

export const isChunkLoadError = (err) => {
  if (!err) return false;
  if (err.name === "ChunkLoadError") return true;
  const msg = err.message || "";
  return /Loading chunk [^\s]+ failed/i.test(msg) || /Loading CSS chunk/i.test(msg);
};

// True when window.error fired because nginx served index.html (HTML body)
// in place of a missing JS chunk — browser parses `<` as JS and throws
// SyntaxError at line 1. We require the filename to look like a chunk so
// we don't reload on real SyntaxErrors in user code.
export const isStaleChunkSyntaxError = ({ message, filename, lineno }) => {
  if (lineno != null && lineno !== 1) return false;
  if (!filename || !/\.(chunk\.)?js(\?.*)?$/i.test(filename)) return false;
  if (!message) return false;
  return /Unexpected token\s+'?</i.test(message) || /Unexpected token\s+<+/i.test(message);
};

// Triggers exactly one hard reload per session. Returns true if a reload was
// initiated (caller should stop further work), false if already attempted or
// sessionStorage is unavailable.
export const reloadOnceForStaleChunk = () => {
  try {
    if (sessionStorage.getItem(RELOAD_FLAG)) return false;
    sessionStorage.setItem(RELOAD_FLAG, "1");
    window.location.reload();
    return true;
  } catch {
    return false;
  }
};

// Call after a successful chunk load so a *future* deploy can re-arm the
// guard. Without this, recovery only works once per session.
export const clearStaleChunkFlag = () => {
  try { sessionStorage.removeItem(RELOAD_FLAG); } catch {}
};
